import { useRef, useState, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { LogOut, ChevronDown, Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccount } from "@/contexts/AccountContext";
import type { Theme } from "@/hooks/useTheme";

interface UserMenuProps {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

export function UserMenu({ theme, setTheme }: UserMenuProps) {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const { account, displayName, email } = useAccount();
  const name = displayName;
  const initial = name.charAt(0).toUpperCase();
  const roles = account?.roles ?? [];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        aria-label="User menu"
      >
        {/* Avatar */}
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-cyan/20 text-brand-cyan font-semibold text-[12px] shrink-0">
          {initial}
        </span>
        <span className="hidden sm:block max-w-64 truncate font-medium">{name}</span>
        <ChevronDown
          className={cn("h-3 w-3 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-[calc(100vw-1.5rem)] sm:w-80 max-w-sm bg-surface-elevated border border-border rounded-lg shadow-xl shadow-black/20 z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-blue/20 text-accent-blue font-semibold shrink-0">
                {initial}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-primary truncate">{name}</p>
                {email && <p className="text-xs text-muted truncate">{email}</p>}
                {roles.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {roles.map((role) => (
                      <span
                        key={role}
                        className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-brand-cyan/10 text-brand-cyan"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile */}
          <div className="py-1">
            {!auth.isAuthenticated && (
              <div className="px-4 py-2 text-sm font-medium text-secondary">Not signed in</div>
            )}
          </div>

          {/* Theme */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs text-muted mb-2">Theme</p>
            <div className="flex gap-1">
              {(
                [
                  { value: "light", icon: Sun, label: "Light" },
                  { value: "system", icon: Monitor, label: "System" },
                  { value: "dark", icon: Moon, label: "Dark" },
                ] as const
              ).map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={cn(
                    "flex flex-1 items-center justify-center gap-1.5 px-2 py-1.5 rounded text-xs font-medium transition-colors cursor-pointer",
                    theme === value
                      ? "bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30"
                      : "text-secondary hover:text-primary hover:bg-surface border border-transparent",
                  )}
                  aria-pressed={theme === value}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Sign out */}
          <div className="border-t border-border py-1">
            <button
              onClick={() => {
                setOpen(false);
                void auth.removeUser().then(() => {
                  window.location.replace("/login");
                });
              }}
              disabled={!auth.isAuthenticated}
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-secondary hover:text-primary hover:bg-surface transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <LogOut className="h-3.5 w-3.5 shrink-0" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
